/**
 * Source metadata for a browser File.
 * Duration for MP4/MOV/M4V is read from the mvhd atom so HEVC iPhone clips
 * (which Chrome often cannot decode) still get a duration. The video element
 * is only used for resolution, and as a duration fallback for other formats.
 */

const MOOV_SCAN_BYTES = 256 * 1024;
const MAX_TOP_LEVEL_BOXES = 48;
const VIDEO_META_TIMEOUT_MS = 8000;
const VIDEO_META_TIMEOUT_WHEN_CONTAINER_KNOWN_MS = 1500;

function fourcc(bytes, offset) {
  return String.fromCharCode(bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3]);
}

function readUint64(view, offset) {
  const hi = view.getUint32(offset);
  const lo = view.getUint32(offset + 4);
  return hi * 4294967296 + lo;
}

/**
 * mvhd payload starts at the version byte (4 bytes after the 'mvhd' fourcc).
 * @param {Uint8Array} bytes
 * @param {number} typeOffset index of the 'mvhd' fourcc
 * @returns {number | null}
 */
export function durationSecondsFromMvhd(bytes, typeOffset) {
  if (typeOffset < 4 || typeOffset + 8 > bytes.length) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const boxSize = view.getUint32(typeOffset - 4);
  if (boxSize < 32 || boxSize > 256) return null;
  const version = bytes[typeOffset + 4];
  const base = typeOffset + 4;
  let timescale = 0;
  let duration = 0;
  if (version === 0) {
    if (base + 20 > bytes.length) return null;
    timescale = view.getUint32(base + 12);
    duration = view.getUint32(base + 16);
  } else if (version === 1) {
    if (base + 32 > bytes.length) return null;
    timescale = view.getUint32(base + 20);
    duration = readUint64(view, base + 24);
  } else {
    return null;
  }
  if (!timescale || !Number.isFinite(duration) || duration <= 0) return null;
  const seconds = duration / timescale;
  if (!Number.isFinite(seconds) || seconds <= 0 || seconds > 1e7) return null;
  return seconds;
}

/**
 * mvhd is a direct child of moov. Walk those children instead of searching
 * for the four letters, so an earlier atom that happens to contain them
 * cannot set the duration.
 * @param {Uint8Array} chunk moov box, possibly truncated to the scan window
 * @returns {number | null}
 */
function durationFromMoovChunk(chunk) {
  if (!chunk || chunk.length < 16) return null;
  const view = new DataView(chunk.buffer, chunk.byteOffset, chunk.byteLength);
  let headerLen = 8;
  let moovSize = view.getUint32(0);
  if (moovSize === 1) {
    moovSize = readUint64(view, 8);
    headerLen = 16;
  } else if (moovSize === 0) {
    moovSize = chunk.length;
  }
  if (!Number.isFinite(moovSize) || moovSize < headerLen) return null;
  let child = headerLen;
  const end = Math.min(chunk.length, moovSize);
  for (let i = 0; i < MAX_TOP_LEVEL_BOXES && child + 8 <= end; i += 1) {
    let childSize = view.getUint32(child);
    const childType = fourcc(chunk, child + 4);
    let childHeader = 8;
    if (childSize === 1) {
      if (child + 16 > end) return null;
      childSize = readUint64(view, child + 8);
      childHeader = 16;
    } else if (childSize === 0) {
      childSize = end - child;
    }
    if (!Number.isFinite(childSize) || childSize < childHeader || child + childSize > end + 1) return null;
    if (childType === 'mvhd') return durationSecondsFromMvhd(chunk, child + 4);
    child += childSize;
  }
  return null;
}

function sliceToArrayBuffer(blob) {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Could not read video bytes'));
    reader.readAsArrayBuffer(blob);
  });
}

async function readSlice(file, start, end) {
  const buf = await sliceToArrayBuffer(file.slice(start, Math.min(end, file.size)));
  return new Uint8Array(buf);
}

/**
 * Walk top-level ISO BMFF / QuickTime atoms and read mvhd inside moov.
 * Does not decode the video. Skips large mdat bodies.
 * @param {Blob} file
 * @returns {Promise<number | null>}
 */
export async function readIsoBmffDurationSeconds(file) {
  if (!file || typeof file.slice !== 'function' || !file.size) return null;
  let offset = 0;
  for (let i = 0; i < MAX_TOP_LEVEL_BOXES && offset + 8 <= file.size; i += 1) {
    const head = await readSlice(file, offset, offset + 16);
    if (head.length < 8) return null;
    const view = new DataView(head.buffer, head.byteOffset, head.byteLength);
    let size = view.getUint32(0);
    const type = fourcc(head, 4);
    let headerLen = 8;
    if (size === 1) {
      if (head.length < 16) return null;
      size = readUint64(view, 8);
      headerLen = 16;
    } else if (size === 0) {
      size = file.size - offset;
    }
    if (!Number.isFinite(size) || size < headerLen || offset + size > file.size + 1) return null;

    if (type === 'moov') {
      const chunk = await readSlice(file, offset, offset + Math.min(size, MOOV_SCAN_BYTES));
      return durationFromMoovChunk(chunk);
    }

    offset += size;
  }
  return null;
}

function readVideoElementMetadata(file, timeoutMs) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve({ durationSeconds: null, sourceVideoResolution: null });
  }
  return new Promise((resolve) => {
    let settled = false;
    let url = '';
    const video = document.createElement('video');
    video.preload = 'metadata';
    const finish = (durationSeconds, sourceVideoResolution) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (url) URL.revokeObjectURL(url);
      resolve({ durationSeconds, sourceVideoResolution });
    };
    const timer = window.setTimeout(() => finish(null, null), timeoutMs);
    try {
      url = URL.createObjectURL(file);
      video.onloadedmetadata = () => {
        const d = Number(video.duration);
        const durationSeconds = Number.isFinite(d) && d > 0 ? d : null;
        const w = Number(video.videoWidth);
        const h = Number(video.videoHeight);
        const sourceVideoResolution =
          Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0
            ? `${Math.round(w)}x${Math.round(h)}`
            : null;
        finish(durationSeconds, sourceVideoResolution);
      };
      video.onerror = () => finish(null, null);
      video.src = url;
    } catch {
      finish(null, null);
    }
  });
}

/**
 * @param {File} file
 * @returns {Promise<{ durationSeconds: number | null, sourceVideoResolution: string | null }>}
 */
export async function readVideoFileSourceMetadata(file) {
  if (!file || typeof file !== 'object') {
    return { durationSeconds: null, sourceVideoResolution: null };
  }
  let containerDuration = null;
  try {
    containerDuration = await readIsoBmffDurationSeconds(file);
  } catch {
    containerDuration = null;
  }
  const elementMeta = await readVideoElementMetadata(
    file,
    containerDuration != null ? VIDEO_META_TIMEOUT_WHEN_CONTAINER_KNOWN_MS : VIDEO_META_TIMEOUT_MS,
  );
  const elementDuration =
    Number.isFinite(elementMeta.durationSeconds) && elementMeta.durationSeconds > 0
      ? elementMeta.durationSeconds
      : null;
  return {
    durationSeconds: elementDuration ?? containerDuration,
    sourceVideoResolution: elementMeta.sourceVideoResolution,
  };
}

/**
 * @param {File} file
 * @returns {Promise<number | null>}
 */
export function readVideoFileDurationSeconds(file) {
  return readVideoFileSourceMetadata(file).then((m) => m?.durationSeconds ?? null);
}
